/**
 * Ticket chat toolbar: one bordered segment of square, quiet icon buttons.
 *
 * Every action shares one color (textSecondary at rest, text on hover) so the bar reads
 * as a single control instead of a row of differently-tinted circles. Violet appears only
 * on focus; orange is left to the resolver state tag.
 */
import type { SxProps, Theme } from "@mui/material";
import { NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";

export const TOOLBAR_BUTTON_SIZE = 28;
export const TOOLBAR_BUTTON_SIZE_COMPACT = 24;
export const TOOLBAR_ICON_SIZE = 17;
export const TOOLBAR_ICON_SIZE_COMPACT = 14;

export function toolbarButtonSize(compact: boolean): number {
  return compact ? TOOLBAR_BUTTON_SIZE_COMPACT : TOOLBAR_BUTTON_SIZE;
}

export function toolbarIconSize(compact: boolean): number {
  return compact ? TOOLBAR_ICON_SIZE_COMPACT : TOOLBAR_ICON_SIZE;
}

/** A button inside the segment: no own border or radius, the group draws both. */
export function toolbarButtonSx(compact: boolean): SxProps<Theme> {
  const size = toolbarButtonSize(compact);
  return {
    width: size,
    height: size,
    p: 0,
    borderRadius: 0,
    color: NEUTRAL.textSecondary,
    transition: "background-color 120ms ease, color 120ms ease",
    "&:hover": { backgroundColor: NEUTRAL.hover, color: NEUTRAL.text },
    "&:active": { backgroundColor: NEUTRAL.border },
    "&.Mui-focusVisible": { boxShadow: `inset 0 0 0 2px ${VIOLET.main}` },
    "&.Mui-disabled": { color: NEUTRAL.borderStrong },
  };
}

/** A standalone square button outside the segment (e.g. the panel chevron). */
export function toolbarStandaloneButtonSx(compact: boolean): SxProps<Theme> {
  return {
    ...(toolbarButtonSx(compact) as object),
    borderRadius: `${RADIUS.control}px`,
  };
}

/**
 * The segment. Tooltips wrap buttons in a <span>, so dividers are drawn on whichever
 * element is the direct child; a hidden action leaves no doubled divider behind.
 */
export const TOOLBAR_GROUP_SX: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "stretch",
  flexShrink: 0,
  border: `1px solid ${NEUTRAL.border}`,
  borderRadius: `${RADIUS.control}px`,
  backgroundColor: NEUTRAL.surface,
  overflow: "hidden",
  "& > *": { display: "inline-flex" },
  "& > * + *": { borderLeft: `1px solid ${NEUTRAL.border}` },
  "&:empty": { display: "none" },
};
